import { useEffect, useState, useCallback, useRef } from "react";
import { AutocompleteBalanceResponse } from "@codway-dev/shared";
import { AutocompleteService } from "../services/autocomplete.service";
import { buildAdminAppUrl } from "../services/admin-deeplink";

interface AutocompleteState {
  data: AutocompleteBalanceResponse | null;
  loading: boolean;
  reloading: boolean;
  error: string | null;
  toggling: boolean;
  buying: string | null;
}

interface AutocompleteActions {
  toggle: (enabled: boolean) => Promise<void>;
  buyPack: (package_id: string) => Promise<void>;
  reload: () => void;
}

export function useAutocomplete(): AutocompleteState & AutocompleteActions {
  const [state, setState] = useState<AutocompleteState>({
    data: null,
    loading: true,
    reloading: false,
    error: null,
    toggling: false,
    buying: null,
  });

  const [reloadSignal, setReloadSignal] = useState(0);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const isFirstLoad = !hasLoadedRef.current;
      setState((s) => ({
        ...s,
        loading: isFirstLoad,
        reloading: !isFirstLoad,
        error: null,
      }));
      try {
        const data = await AutocompleteService.getBalance();
        if (!cancelled) {
          hasLoadedRef.current = true;
          setState((s) => ({ ...s, data, loading: false, reloading: false }));
        }
      } catch (err) {
        if (!cancelled)
          setState((s) => ({
            ...s,
            loading: false,
            reloading: false,
            error: err instanceof Error ? err.message : "Error desconocido",
          }));
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [reloadSignal]);

  // Reload when the tab regains focus — covers the case where the merchant
  // approves a purchase in the Shopify billing tab and then closes it.
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        setReloadSignal((n) => n + 1);
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  const reload = useCallback(() => setReloadSignal((n) => n + 1), []);

  const toggle = useCallback(async (enabled: boolean) => {
    setState((s) => ({ ...s, toggling: true, error: null }));
    try {
      const config = await AutocompleteService.updateConfig(enabled);
      setState((s) =>
        s.data
          ? {
              ...s,
              toggling: false,
              data: {
                ...s.data,
                enabled: config.enabled,
                credits: config.credits,
                sessions_used: config.sessions_used,
              },
            }
          : { ...s, toggling: false }
      );
    } catch (err) {
      setState((s) => ({
        ...s,
        toggling: false,
        error: err instanceof Error ? err.message : "Error al actualizar",
      }));
      throw err;
    }
  }, []);

  const buyPack = useCallback(async (package_id: string) => {
    setState((s) => ({ ...s, buying: package_id, error: null }));
    try {
      const returnUrl = buildAdminAppUrl("autocomplete");
      const { confirmation_url } = await AutocompleteService.topup(
        package_id,
        returnUrl
      );
      localStorage.setItem("codway_autocomplete_billing_pending", "1");
      window.open(confirmation_url, "_top");
    } catch (err) {
      setState((s) => ({
        ...s,
        buying: null,
        error: err instanceof Error ? err.message : "Error al iniciar compra",
      }));
      return;
    }
    setState((s) => ({ ...s, buying: null }));
  }, []);

  return { ...state, toggle, buyPack, reload };
}
